// src/components/common/ProtectedSidebarItem.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

export const ProtectedSidebarItem = ({
  item,
  isCollapsed,
  openDropdown,
  setOpenDropdown,
  onItemClick,
  iconComponent: IconComponent,
  level = 0,
  openReportsSubmenu, // Specific state for Reports sub-menus
  setOpenReportsSubmenu, // Setter for Reports sub-menus
}) => {
  const location = useLocation();
  // For nested dropdowns deeper than the first level, we manage their state locally.
  const [localOpenDropdown, setLocalOpenDropdown] = useState(null);

  // Determine if this specific dropdown is open.
  const isOpen = level === 0
    ? openDropdown === item.name
    : level === 1 && item.parentName === 'Reports'
      ? openReportsSubmenu === item.name
      : localOpenDropdown === item.name;

  // Check permissions for the current item.
  const parentPerm = item.resource ? usePermissions(item.resource) : { canRead: true };

  /**
   * Recursively filters children and adds a flag to identify items within the 'Reports' menu.
   * @param {Array} children - The array of child menu items.
   * @param {boolean} isInsideReports - Flag indicating if the parent is the 'Reports' menu.
   * @returns {Array} - A filtered array of visible children with an `isInsideReports` flag.
   */
  const getVisibleChildren = (children, isInsideReports = false) => {
    if (!children) return [];

    return children
      .map(child => {
        const perm = child.resource ? usePermissions(child.resource) : { canRead: true };

        // Determine if the current child is inside the Reports menu
        const childIsInsideReports = isInsideReports || item.name === 'Reports';

        const visibleGrandChildren = child.isDropdown ? getVisibleChildren(child.children, childIsInsideReports) : [];

        return {
          ...child,
          perm,
          hasVisibleChildren: visibleGrandChildren.length > 0,
          isInsideReports: childIsInsideReports, // Add the flag here
        };
      })
      .filter(child => {
        // Show if user has permission AND (it's not a dropdown OR it has visible children)
        return child.perm.canRead && (!child.isDropdown || child.hasVisibleChildren);
      });
  };

  /**
   * Recursively collects all leaf nodes (items without children) from the menu structure.
   * @param {Array} children - The array of child menu items.
   * @returns {Array} - An array of leaf nodes with their path and tooltip.
   */
  const getAllLeafNodes = (children) => {
    if (!children) return [];

    let leafNodes = [];

    children.forEach(child => {
      if (child.isDropdown) {
        leafNodes = [...leafNodes, ...getAllLeafNodes(child.children)];
      } else {
        leafNodes.push({
          name: child.name,
          path: child.path,
          tooltip: child.tooltip,
          isExternal: child.isExternal
        });
      }
    });

    return leafNodes;
  };

  const visibleChildren = getVisibleChildren(item.children);
  const hasAccess = parentPerm.canRead && (!item.isDropdown || visibleChildren.length > 0);

  if (!hasAccess) return null;

  const isActive = (path) => location.pathname === path;
  const Icon = IconComponent ? (props) => <IconComponent icon={item.icon} {...props} /> : item.icon;

  const getIconClasses = () => {
    const base = "w-5 h-5 flex-shrink-0 transition-all duration-300";
    const animations = {
      Settings: "hover:animate-spin",
      LayoutDashboard: "hover:animate-bounce",
      Ticket: "hover:animate-pulse",
      Logs: "hover:animate-wobble",
      UtensilsCrossed: "hover:animate-ping",
      HelpingHand: "hover:animate-wave",
      NotebookPen: "hover:animate-write",
      default: "hover:scale-125 hover:rotate-12",
    };
    const anim = animations[item.icon.displayName || item.icon.name] || animations.default;
    return cn(base, anim);
  };

  const getIndentClass = () => {
    // If this item or its parent is the 'Reports' menu, no indentation.
    if (item.name === 'Reports' || item.isInsideReports) {
      return '';
    }

    if (level === 0) return '';
    // Written out in full: Tailwind scans the source for class names, so a template
    // literal like `ml-${n}` produces nothing and the indentation silently vanished.
    return level === 1 ? 'ml-4' : level === 2 ? 'ml-8' : 'ml-12';
  };


  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (level === 0) {
      setOpenDropdown(prev => (prev === item.name ? null : item.name));
      if (item.name === 'Reports' && openDropdown === 'Reports') {
        setOpenReportsSubmenu(null);
      }
    } else if (level === 1 && item.parentName === 'Reports') {
      setOpenReportsSubmenu(prev => (prev === item.name ? null : item.name));
    } else {
      setLocalOpenDropdown(prev => (prev === item.name ? null : item.name));
    }
  };

  const handleChildClick = () => {
    onItemClick?.();
  };

  // --- RENDERING LOGIC ---

  // 1. External Link
  if (item.isExternal || (item.path && item.path.startsWith('http'))) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleChildClick}
            className={cn(
              'flex items-center gap-[10px] min-h-[42px] shrink-0 px-3 py-[5px] rounded-[12px] text-[13.5px] leading-[16px] transition-all group',
              'hover:bg-white/10 text-white',
              isCollapsed && level === 0 ? 'justify-center' : 'justify-start',
              !isCollapsed && getIndentClass()
            )}
          >
            {level === 0 && <Icon className={getIconClasses()} />}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.name}</span>
                <ExternalLink className="w-4 h-4 opacity-60" />
              </>
            )}
          </a>
        </TooltipTrigger>
        {isCollapsed && level === 0 && <TooltipContent side="right">{item.tooltip}</TooltipContent>}
      </Tooltip>
    );
  }

  // 2. Single Item (not a dropdown)
  if (!item.isDropdown) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.path}
            onClick={handleChildClick}
            className={cn(
              'flex items-center gap-[10px] min-h-[42px] shrink-0 px-3 py-[5px] rounded-[12px] text-[13.5px] leading-[16px] transition-all group',
              isActive(item.path)
                ? 'bg-white/[0.18] text-white'
                : 'text-white hover:bg-white/10',
              isCollapsed && level === 0 ? 'justify-center' : 'justify-start',
              level === 2 && '!ml-0',

              !isCollapsed && getIndentClass()
            )}
          >
            {level === 0 && <Icon className={getIconClasses()} />}
            {!isCollapsed && <span className="text-left">{item.name}</span>}
          </Link>
        </TooltipTrigger>
        {isCollapsed && level === 0 && <TooltipContent side="right">{item.tooltip}</TooltipContent>}
      </Tooltip>
    );
  }

  // 3. Dropdown (has children) - RECURSIVE CASE
  return (
    <div className={cn("w-full", !isCollapsed && getIndentClass(), level > 0 && "mt-1")}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleToggle}
            className={cn(
              'w-full flex items-center min-h-[42px] shrink-0 px-3 py-[5px] rounded-[12px] text-[13.5px] leading-[16px] transition-all group',
              'text-white hover:bg-white/10',
              isCollapsed && level === 0 ? 'justify-center' : 'justify-between'
            )}
          >
            <div className="flex items-center gap-[10px] min-w-0">
              {level === 0 && <Icon className={getIconClasses()} />}
              {!isCollapsed && <span className="text-left">{item.name}</span>}
            </div>

            {!isCollapsed && (
              <div className="w-5 h-5 flex items-center justify-center">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            )}
          </button>
        </TooltipTrigger>
        {isCollapsed && level === 0 && <TooltipContent side="right">{item.tooltip}</TooltipContent>}
      </Tooltip>

      {/* Bullet points for collapsed sidebar - displayed below the menu item */}
      {isCollapsed && level === 0 && isOpen && (
        <div className="flex justify-center mt-2 mb-4">
          <div className="flex flex-col items-center gap-5 bg-slate-800 p-3 rounded-lg shadow-xl border border-white/10">
            {item.name === 'Reports' ? (
              getAllLeafNodes(visibleChildren).map((leafNode, idx) => (
                <Tooltip key={leafNode.path || leafNode.name}>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      {leafNode.isExternal || leafNode.path?.startsWith('http') ? (
                        <a
                          href={leafNode.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick();
                          }}
                          className="block"
                        >
                          <div
                            className={cn(
                              "w-2.5 h-2.5 rounded-full transition-all hover:scale-150 shadow-lg",
                              idx % 3 === 0 ? "bg-primary" : idx % 3 === 1 ? "bg-primary/80" : "bg-primary/60"
                            )}
                          />
                        </a>
                      ) : (
                        <Link
                          to={leafNode.path}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick();
                          }}
                          className="block"
                        >
                          <div
                            className={cn(
                              "w-2.5 h-2.5 rounded-full transition-all hover:scale-150 shadow-lg",
                              idx % 3 === 0 ? "bg-primary" : idx % 3 === 1 ? "bg-primary/80" : "bg-primary/60"
                            )}
                          />
                        </Link>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs z-50 max-w-xs">
                    {leafNode.tooltip || leafNode.name}
                    {leafNode.isExternal && " ↗"}
                  </TooltipContent>
                </Tooltip>
              ))
            ) : (
              visibleChildren.map((child, idx) => (
                <Tooltip key={child.path || child.name}>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      {child.isExternal || child.path?.startsWith('http') ? (
                        <a
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick();
                          }}
                          className="block"
                        >
                          <div
                            className={cn(
                              "w-2.5 h-2.5 rounded-full transition-all hover:scale-150 shadow-lg",
                              idx === 0 ? "bg-primary" : idx === 1 ? "bg-primary/80" : "bg-primary/60"
                            )}
                          />
                        </a>
                      ) : (
                        <Link
                          to={child.path}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildClick();
                          }}
                          className="block"
                        >
                          <div
                            className={cn(
                              "w-2.5 h-2.5 rounded-full transition-all hover:scale-150 shadow-lg",
                              idx === 0 ? "bg-primary" : idx === 1 ? "bg-primary/80" : "bg-primary/60"
                            )}
                          />
                        </Link>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs z-50">
                    {child.tooltip || child.name}
                    {child.isExternal && " ↗"}
                  </TooltipContent>
                </Tooltip>
              ))
            )}
          </div>
        </div>
      )}

      {/* Children: Render recursively if not collapsed and this dropdown is open */}
      {!isCollapsed && isOpen && visibleChildren.length > 0 && (
        <div className="mt-1 space-y-1 border-l border-white/10 pl-4">
          {visibleChildren.map((child) => (
            <ProtectedSidebarItem
              key={child.path || child.name}
              item={child}
              isCollapsed={isCollapsed}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onItemClick={handleChildClick}
              iconComponent={IconComponent}
              level={level + 1}
              openReportsSubmenu={openReportsSubmenu}
              setOpenReportsSubmenu={setOpenReportsSubmenu}
            />
          ))}
        </div>
      )}
    </div>
  );
};
